import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { Button, Modal } from '@bettercomp/ui-components';
import { useAuthContext } from '@bettercomp/ams-spa-auth-react';
import { logout } from 'service/cognitoService';
import { appLogout } from 'service/appLogout';
import { createAuthBroadcastChannel, AuthBroadcastMessageType } from './constants';
import cn from './styles.module.scss';

const IDLE_LOGOUT_TIMEOUT = 30 * 60 * 1000;
const IDLE_WARNING_TIMEOUT = IDLE_LOGOUT_TIMEOUT - 3 * 60 * 1000;

export const IdleWarning = () => {
  const [visible, setVisible] = useState(false);
  // Latest-ref: resetTimer reads the warning state through it, so neither its
  // identity nor the arming effect below depends on the modal toggling.
  const visibleRef = useRef(false);
  const logoutTimeout = useRef<ReturnType<typeof setTimeout>>();
  const idleWarningTimeout = useRef<ReturnType<typeof setTimeout>>();
  const channel = useMemo(() => createAuthBroadcastChannel(), []);
  const { session } = useAuthContext();
  // Latest-ref: the armed 30-minute timeout must see the session that exists
  // when it fires, not the one captured when the timer was scheduled.
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const setWarningVisible = useCallback((nextVisible: boolean) => {
    visibleRef.current = nextVisible;
    setVisible(nextVisible);
  }, []);

  const onLogoutTimeout = useCallback(() => {
    // Mode-aware: an AMS session revokes and broadcasts on its own channel;
    // the idle Up/Logout messaging below stays as is either way.
    appLogout(sessionRef.current);
  }, []);

  const clearTimers = useCallback(() => {
    clearTimeout(idleWarningTimeout.current);
    clearTimeout(logoutTimeout.current);
  }, []);

  // Plain activity never extends a session that is already warned; only
  // "Keep Me Logged in" or a sibling tab's Up forces the reset.
  const resetTimer = useCallback((force: boolean = false) => {
    if (!force && visibleRef.current) {
      return;
    }
    clearTimers();
    idleWarningTimeout.current = setTimeout(() => setWarningVisible(true), IDLE_WARNING_TIMEOUT);
    logoutTimeout.current = setTimeout(onLogoutTimeout, IDLE_LOGOUT_TIMEOUT);
  }, [clearTimers, setWarningVisible, onLogoutTimeout]);

  // Armed once per mount, disarmed on unmount. IdleWarning renders inside the
  // per-page layout, so a page with another layout (the full-screen SRW
  // wizard) remounts it; a timer the old instance left behind logged the user
  // out 30 minutes after that navigation however active they were, with no
  // mounted modal left to warn them (174641).
  useEffect(() => {
    resetTimer(true);
    return clearTimers;
  }, [resetTimer, clearTimers]);

  useEffect(() => () => {
    channel.close();
  }, [channel]);

  useEffect(() => {
    // handle clicks, that comes from another tab
    const authChannelMessageHandler = (message: { data: AuthBroadcastMessageType }) => {
      if (message.data === AuthBroadcastMessageType.Up) {
        setWarningVisible(false);
        resetTimer(true);
      }
      if (message.data === AuthBroadcastMessageType.Logout) {
        logout(false);
      }
    };

    const activityHandler = () => {
      // notify other tabs about clicks
      channel.postMessage(AuthBroadcastMessageType.Up);
      resetTimer();
    };

    channel.addEventListener('message', authChannelMessageHandler);
    document.addEventListener('click', activityHandler);

    return () => {
      channel.removeEventListener('message', authChannelMessageHandler);
      document.removeEventListener('click', activityHandler);
    };
  }, [channel, resetTimer, setWarningVisible]);

  // The button click also bubbles to the document listener above; that Up is
  // what lets a sibling tab warned at the same time dismiss and re-arm.
  const handleConfirm = () => {
    setWarningVisible(false);
    resetTimer(true);
  };

  return (
    <Modal
      open={visible}
      closable={false}
      centered
    >
      <div className={cn.container}>
        <div>Your session will expire soon</div>
        <Button type="primary" onClick={handleConfirm} size="large">Keep Me Logged in</Button>
      </div>
    </Modal>
  );
};
